import React from 'react'
import { keyframes, styled } from 'styled-components'

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const SkeletonRow = styled.tr<{ $cardStyleRows?: boolean }>`
  ${({ $cardStyleRows, theme }) =>
    $cardStyleRows
      ? `background-color: ${theme.forecast.cardBackground};`
      : `
      background-color: transparent;
      &:nth-child(odd) {
        background-color: ${theme.forecast.cardGroupBackground};
      }
    `}
`

const SkeletonCell = styled.td`
  padding: var(--table-cell-padding, 0.5rem);
  min-height: 3.5rem;
  border-style: hidden !important;
`

const ShimmerBar = styled.div<{ $width?: string }>`
  height: 0.75rem;
  width: ${({ $width }) => $width ?? '70%'};
  border-radius: 0.25rem;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.forecast.cardGroupBackground} 25%,
    ${({ theme }) => theme.forecast.hoverButton} 50%,
    ${({ theme }) => theme.forecast.cardGroupBackground} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

/** Each column gets a fixed width; columns cycle through these values. */
const COLUMN_WIDTHS = ['70%', '55%', '80%', '45%', '65%', '50%', '75%', '60%']

interface TableSkeletonRowsProps {
  columnCount: number
  rowCount?: number
  cardStyleRows?: boolean
}

/**
 * Renders placeholder rows with shimmer bars that preserve the table structure
 * while data is loading. Every row in a given column shares the same bar width,
 * mimicking how real table data fills each column consistently.
 */
export const TableSkeletonRows: React.FC<TableSkeletonRowsProps> = ({
  columnCount,
  rowCount = 16,
  cardStyleRows = true,
}) => {
  return (
    <>
      {Array.from({ length: rowCount }, (_, rowIndex) => (
        <SkeletonRow key={rowIndex} $cardStyleRows={cardStyleRows}>
          {Array.from({ length: columnCount }, (_, colIndex) => (
            <SkeletonCell key={colIndex}>
              <ShimmerBar
                $width={COLUMN_WIDTHS[colIndex % COLUMN_WIDTHS.length]}
              />
            </SkeletonCell>
          ))}
        </SkeletonRow>
      ))}
    </>
  )
}
